<template>
  <div class="p-content-page">
    <page-title>Search Results</page-title>

    <loading v-if="loading" />

    <div class="row" v-else>
      <div class="col-xs-12 col-md-8">
        <section-block class="p-0" :headerHidden="!searchTerm">
          <template slot="header" v-if="searchTerm">
            <p>
              Your search for
              <em>{{ searchTerm }}</em> returned
              <em>{{ totalResults }}</em> results
            </p>
          </template>

          <template slot="actions" v-if="searchTerm && totalResults > 0">
            <div class="c-filter-bar">

              <form-multiselect
                :show-valid-tick="false"
                :show-drop-icon="true"
                ref="filter"
                ghost
                placeholder="Filter By"
                v-model="filter"
                :options="filterOptions"
                :multiple="true"
                label="name"
                track-by="name"
                :searchable="true"
                :long="true"
                @input="updateFilter"
              ></form-multiselect>
            </div>
          </template>
          <article v-if="searchTerm && totalResults > 0">
            <search-results-block
              title="External Systems"
              :results="searchResults.ExternalSystems"
              type="external"
              @click="handleExternalLink"
              v-if="searchResults.ExternalSystems.length > 0"
              v-show="display.ExternalSystems"
            ></search-results-block>
            <search-results-block
              title="Policies"
              :results="searchResults.Policies"
              type="policy"
              @click="handlePolicyClick"
              v-if="searchResults.Policies.length > 0"
              v-show="display.Policies"
            ></search-results-block>
            <search-results-block
              title="Forms"
              :results="searchResults.Forms"
              type="form"
              @click="handleClick"
              v-if="searchResults.Forms.length > 0"
              v-show="display.Forms"
            ></search-results-block>
            <search-results-block
              title="Pages"
              :results="searchResults.Pages"
              type="page"
              @click="handleClick"
              v-if="searchResults.Pages.length > 0"
              v-show="display.Pages"
            ></search-results-block>
            <search-results-block
              title="News"
              :results="searchResults.News"
              type="news"
              @click="handleClick"
              v-if="searchResults.News.length > 0"
              v-show="display.News"
            ></search-results-block>
            <search-results-block
              title="Events"
              :results="searchResults.Events"
              type="events"
              @click="handleClick"
              v-if="searchResults.Events.length > 0"
              v-show="display.Events"
            ></search-results-block>
            <search-results-block
              title="Questions"
              :results="searchResults.Questions"
              type="questions"
              @click="handleClick"
              v-if="searchResults.Questions.length > 0"
              v-show="display.Questions"
            ></search-results-block>
          </article>
          <div v-else-if="totalResults < 1 && searchTerm" class="p-bottom-20">
            <p>There were no results for your query. Please try again.</p>
            <site-search-bar />
          </div>
          <div v-else>
            <p>Please use this form to search</p>
            <site-search-bar inline />
          </div>
        </section-block>
      </div>
      <div class="u-hide-mobile col-md-4">
        <section-block headerHidden>
          <profile-card large noBorder />
        </section-block>

        <base-container title="Latest News" viewLink="/news" withGradient>
          <base-scroll-list>
            <ol v-if="articles.length > 0">
              <li v-for="(article, index) in articles" :key="index">
                <card-list
                  v-bind="article"
                  @click="handleNewsClick(article)"
                ></card-list>
              </li>
            </ol>
            <p v-else>There are no articles</p>
          </base-scroll-list>
        </base-container>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import querySearch from "@/mixins/querySearch";

const SiteSearchBar = () => import("@/components/SiteSearchBar");
const CardList = () => import("@/components/Containers/BaseCardList");
import {
  ProfileCard,
  BaseContainer,
  BaseScrollableList as BaseScrollList,
} from "@/components/Containers";
const SearchResultsBlock = () => import("@/components/SearchResultsBlock");
export default {
  name: "Search",
  mixins: [querySearch],
  components: {
    BaseContainer,
    BaseScrollList,
    ProfileCard,
    CardList,
    SearchResultsBlock,
    SiteSearchBar,
  },

  data() {
    return {
      loading: true,
      searchTerm: "",
      filter: [],
      display: {
        Policies: true,
        Forms: false,
        Pages: true,
        News: true,
        Events: true,
        Questions: true,
        ExternalSystems: true,
      },
    };
  },

  computed: {
    ...mapState("News", ["articles"]),
    ...mapState("search", ["searchQuery", "searchResults"]),
    ...mapGetters("search", ["totalResults"]),
    filterOptions() {
      const keys = Object.keys(this.searchResults).filter(
        (elem) => this.searchResults[elem].length > 0
      );
      return keys.map((key) => ({ id: key, name: key }));
    },
  },
  methods: {
    handlePolicyClick(result, $event) {
      const link = `/documents/policies/${result.id}`;
      this.$router.push({ path: link });
    },
    handleExternalLink(result, $event) {
      window.open(result.link);
    },
    handleClick(result, $event) {
      //external news item
      if (result.website_url) {
        window.open(result.website_url);
        return;
      }

      let link = result.self;
      if (result.page_url) {
        link = `/content${result.page_url}`;
      }

      this.$router.push({ path: link });
    },
    updateFilter(val) {
      let selected = val.map((v) => v.name);
      //if the above is empty, then just toggle all to true
      if (selected.length < 1) {
        selected = Object.keys(this.display);
      }
      Object.keys(this.display).forEach((val) => {
        if (!selected.includes(val)) {
          this.display[val] = false;
        } else {
          this.display[val] = true;
        }
      });
    },
  },
  created() {
    this.$store.commit("News/SET_RESULT_COUNT", 5);
    this.$store
      .dispatch("News/list")
      .then(() => {})
      .catch(() => console.log("news fail"));

    // do we have stuff in local storage?
    if (window.localStorage) {
      if (window.localStorage.getItem("mftSiteSearch")) {
        const searchForm = JSON.parse(
          window.localStorage.getItem("mftSiteSearch")
        );
        this.$store.commit("search/SET_QUERY", searchForm.searchQuery);
        this.$store.commit("search/SET_TYPE", searchForm.searchType);
        window.localStorage.removeItem("mftSiteSearch");
      }
    }
    this.searchTerm = this.searchQuery;

    // pre-filled from URL
    const { query } = this.$route;

    if (query.search) {
      this.searchTerm = query.search;
      this.$store.commit("search/SET_QUERY", query.search);
    }
    if (query.type) {
      this.$store.commit("search/SET_TYPE", query.type);
    }

    //get search results
    if (this.searchTerm !== "") {
      this.$store.commit("search/CLEAR_RESULTS");
      this.$store.dispatch("search/siteSearch").then((resp) => {
        this.loading = false;
      });
    } else {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
em {
  font-style: normal;
  font-weight: bold;
  font-family: $font-frutiger-bold;
}

.c-filter-bar {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;

  > div {
    border: 1px solid $colour-nhs-pale-grey;
    height: 54px;

    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  &__label,
  &__control {
    height: 54px;
    display: flex;
  }

  &__label {
    padding: 2px 0.65em;
    overflow: hidden;
    min-width: 80px;
    font-size: 16px;
    align-items: center;
    cursor: pointer;
  }

  &__control {
    display: none;
    padding-right: 2em;
    min-width: 100px;
    padding: 2px 0 2px 0.5em;

    border-left: 1px solid $colour-nhs-pale-grey;
  }

  @media screen and (min-width: 1024px) {
    > div {
      border-radius: 2em;
      margin-right: 0.5em;
    }

    &__control {
      display: flex;
    }
  }
}
</style>
